/**
 * 입고작업 2.0(GL17) 화면 상태·동작 — 1.0 GL09 의 스캔·라벨 인쇄·녹화·첨부·저장을 한 훅에 모았다.
 * 엔트리(index.tsx)가 한 번 부르고, wiring.ts 의 슬롯이 이 결과를 읽어 생성본 화면에 잇는다(같은 훅을 슬롯 여러 곳에서 부르지 않는다).
 *
 * 슬롯 규칙: 실패는 안내 뒤 throw, 성공은 이 훅이 큰 토스트(scanNotice)로 알리고 false(생성본 기본 토스트 생략).
 * 1.0 alert 는 모두 토스트로 바꿨다 — 스캐너 앞 작업자가 확인 버튼에 막히지 않게(반품작업_신규 2.0 과 같다).
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { notice, scanFailureError, scanInvalidError, scanNotice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'
import { useMediaCapture } from '@/hooks/inspectionMedia/useMediaCapture'
import type { CommonFileResourceType } from '@/services/commonFile/types'
import {
  DEFAULT_RECORD_SECONDS,
  DEFAULT_RESOLUTION_HEIGHT,
  DEFAULT_RESOLUTION_WIDTH,
  INBOUND_ATTACHMENT_MAX,
  MESSAGES,
  SCAN_RESULT_LABEL,
} from '../consts'
import { parseScanInput } from '../scanInput'
import { buildInboundLabelHtml, type InboundLabelInput, labelPrintCount, toLabelDate } from '../label/inboundLabel'
import { useLabelImages } from '../label/labelImages'
import { printInboundLabel } from '../label/printInboundLabel'
import { type InboundScanRes, type InboundScanRow, toInboundOrderLabels, toInboundScanRows } from '../queries/types'
import { useSaveInboundAttachments, useScanInboundInvoice, useUploadInboundFile } from '../queries/useInboundWorkQueries'

export type AttachmentStatus = 'uploading' | 'done' | 'failed'

/** 저장 전 첨부 한 건 — 찍거나 녹화하는 즉시 S3 에 올리고, [저장]이 올라간 것의 key 로 완료 API 를 불러 원본송장에 붙인다. */
export interface InboundAttachment {
  file: Blob
  fileName: string
  resourceType: CommonFileResourceType
  status: AttachmentStatus
  key?: string
}

/** 표 행(데모 컬럼 키) — 위험물은 1.0 처럼 수량을 붙인다('Y(2)'). */
export const toViewRow = (row: InboundScanRow, index: number): Record<string, unknown> => ({
  key: `${row.INB_TRS_INVOICE}-${index}`,
  INB_TRS_INVOICE: row.INB_TRS_INVOICE,
  ORD_CONSOL_NO: row.ORD_CONSOL_NO,
  OUB_PDATE: row.OUB_PDATE,
  SVC_TYPE: row.SVC_TYPE,
  INV_TYPE: row.INV_TYPE,
  TOTAL_QTY: row.TOTAL_QTY,
  DANGER: row.DANGER === 'Y' ? `Y(${row.DANGER_QTY})` : 'N',
  IS_VARIANT: row.IS_VARIANT,
  ORD_STATUS: row.ORD_STATUS,
})

/** 판정 결과 문구 — '정상 · 송장' + 서버 안내. */
export const toResultText = (scanResult: string | undefined, target: string, message: string): string =>
  [(SCAN_RESULT_LABEL as Record<string, string | undefined>)[scanResult ?? 'NOT_FOUND'] ?? scanResult ?? '', target, message].filter(Boolean).join(' · ')

/** 첨부 대기 문구 — 사진 n장 · 영상 n개 (+ 업로드 중 · 실패). */
export const toAttachText = (list: InboundAttachment[]): string => {
  const photos = list.filter((a) => a.resourceType === 'ATCH_FILE').length
  const videos = list.filter((a) => a.resourceType === 'VIDEO').length
  const uploading = list.filter((a) => a.status === 'uploading').length
  const failed = list.filter((a) => a.status === 'failed').length
  return [`첨부 대기 사진 ${photos}장 · 영상 ${videos}개`, uploading ? `업로드 중 ${uploading}건` : '', failed ? `실패 ${failed}건` : '']
    .filter(Boolean)
    .join(' · ')
}

const digits = (v: string): string => v.replaceAll(/[^0-9]/g, '')

type Field<T> = { value: T; onChange: (v: T) => void }

export interface InboundWorkController {
  invoiceField: Field<string>
  autoRecordField: Field<boolean>
  secondsField: Field<string>
  widthField: Field<string>
  heightField: Field<string>
  rows: Record<string, unknown>[]
  scanning: boolean
  attachText: string
  orgInvoiceText: string
  resultText: string
  recording: {
    preview?: ReactNode
    recording: boolean
    caption: string
    files: string[]
    onCapture: () => void
    onRemoveFile: (index: number) => void
  }
  scan: (value: string) => Promise<false>
  save: () => Promise<false>
  startRecording: () => false | undefined
  stopRecording: () => Promise<string | false>
}

export const useInboundWork = (): InboundWorkController => {
  const [invoice, setInvoice] = useState('')
  const [autoRecord, setAutoRecord] = useState(false)
  const [seconds, setSeconds] = useState(DEFAULT_RECORD_SECONDS)
  const [width, setWidth] = useState(DEFAULT_RESOLUTION_WIDTH)
  const [height, setHeight] = useState(DEFAULT_RESOLUTION_HEIGHT)
  const [orgInvoice, setOrgInvoice] = useState('')
  const [rows, setRows] = useState<InboundScanRow[]>([])
  const [resultText, setResultText] = useState<string>(MESSAGES.scanPrompt)
  const [scanning, setScanning] = useState(false)
  const [attachments, setAttachments] = useState<InboundAttachment[]>([])

  // 비동기 흐름(스캔 응답·저장)이 기다린 뒤에도 최신 값을 읽게 ref 로 함께 든다.
  const orgRef = useRef(orgInvoice)
  orgRef.current = orgInvoice
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments
  const autoRecordRef = useRef(autoRecord)
  autoRecordRef.current = autoRecord
  const scanningRef = useRef(false)
  /** 마지막 입력 원문(1.0 newBarcode) — 저장 실패 라벨에 찍는다. */
  const lastBarcodeRef = useRef('')

  const media = useMediaCapture({
    autoRecordTrigger: false,
    recordSeconds: Number(seconds) || undefined,
    resolution: { width: Number(width) || 0, height: Number(height) || 0 },
    cameraUnavailableMessage: MESSAGES.cameraUnavailable,
  })
  const mediaRef = useRef(media)
  mediaRef.current = media

  const images = useLabelImages()
  const imagesRef = useRef(images)
  imagesRef.current = images

  const { mutateAsync: scanAsync } = useScanInboundInvoice()
  const { mutateAsync: uploadAsync } = useUploadInboundFile()
  const { mutateAsync: saveAsync } = useSaveInboundAttachments()

  // ── 첨부: 촬영·녹화가 생기는 즉시 올린다(1.0 은 녹화본을 스캔 직후 올렸다). 같은 Blob 은 한 번만 ──
  const seenRef = useRef(new WeakSet<Blob>())
  const patchAttachment = useCallback((file: Blob, patch: Partial<InboundAttachment>) => {
    setAttachments((prev) => prev.map((a) => (a.file === file ? { ...a, ...patch } : a)))
  }, [])
  useEffect(() => {
    const fresh = [
      ...media.captures.map((c) => ({ ...c, resourceType: 'ATCH_FILE' as const })),
      ...media.recordings.map((r) => ({ ...r, resourceType: 'VIDEO' as const })),
    ].filter((c) => !seenRef.current.has(c.file))
    if (!fresh.length) return
    fresh.forEach((c) => seenRef.current.add(c.file))
    setAttachments((prev) => [
      ...prev,
      ...fresh.map((c) => ({ file: c.file, fileName: c.fileName, resourceType: c.resourceType, status: 'uploading' as const })),
    ])
    fresh.forEach((c) => {
      uploadAsync({ file: c.file, fileName: c.fileName })
        .then((key) => patchAttachment(c.file, { status: 'done', key }))
        .catch((e: unknown) => {
          patchAttachment(c.file, { status: 'failed' })
          notice.invalid(MESSAGES.uploadFailed(c.fileName, e instanceof Error ? e.message : apiErrorText(e, '')))
        })
    })
  }, [media.captures, media.recordings, uploadAsync, patchAttachment])

  const removeAttachment = useCallback((index: number) => {
    const target = attachmentsRef.current[index]
    if (!target) return
    setAttachments((prev) => prev.filter((a) => a.file !== target.file))
    mediaRef.current.discard([target.file])
  }, [])

  // ── 라벨 인쇄 — 실패는 작은 토스트만(스캔 결과 큰 토스트를 덮지 않는다). 흐름은 막지 않는다 ──
  const printLabel = useCallback((input: Omit<InboundLabelInput, 'date' | 'images'>) => {
    const copies = labelPrintCount(input)
    if (copies <= 0) return
    const html = buildInboundLabelHtml({ ...input, date: toLabelDate(), images: imagesRef.current })
    printInboundLabel(html, copies).catch(() => notice.invalid(MESSAGES.printFailed))
  }, [])

  // ── 자동녹화 — 1.0: Enter 때 켜고, 다른 송장으로 넘어가면 앞 녹화를 끊고 새로 켠다 ──
  const autoRecordFor = useCallback(async (nextInvoice: string) => {
    if (!mediaRef.current.isCameraReady) return
    const changed = orgRef.current !== '' && orgRef.current !== nextInvoice
    if (changed) await mediaRef.current.stopRecording()
    // await 동안 다시 그려졌을 수 있어 최신 훅 결과로 시작한다
    mediaRef.current.startRecording()
  }, [])

  const scan = useCallback(
    async (value: string): Promise<false> => {
      if (scanningRef.current) {
        // 칸을 비운다 — 스캐너는 칸에 이어 쳐서, 안내대로 다시 스캔하면 두 값이 붙은 송장(BB)이 나간다(리뷰 #1130)
        setInvoice('')
        scanNotice.invalid(MESSAGES.scanBusy)
        return false
      }
      const parsed = parseScanInput(value, orgRef.current, rowsRef.current[0])
      setInvoice('')
      if (!parsed.ok) throw scanInvalidError(parsed.message)

      const { request, barcode } = parsed
      lastBarcodeRef.current = barcode
      // 앞 송장 녹화는 다음 스캔 때 끊겨 첨부 대기에 들어가고, 저장할 때의 원본송장(=방금 스캔한 송장)에 붙는다.
      // 1.0 도 다음 스캔 응답의 INB_TRS_INVOICE 에 앞 녹화본을 붙였다(GL09.js:330-401 handleSearch 콜백) — 같은 귀속을 일부러 유지한다.
      if (autoRecordRef.current) autoRecordFor(request.invoiceNumber).catch(() => undefined)

      scanningRef.current = true
      setScanning(true)
      try {
        let res: InboundScanRes | null = null
        let failure = ''
        try {
          res = await scanAsync(request)
          if (!res) failure = MESSAGES.scanEmpty
          else if (!res.succeeded) failure = res.message || MESSAGES.scanNotFound
        } catch (e) {
          failure = apiErrorText(e, MESSAGES.scanFailed)
        }
        // 원본송장 = 방금 스캔 API 에 보낸 송장. 실패여도 바뀐다(1.0 componentWillReceiveProps).
        setOrgInvoice(request.invoiceNumber)
        orgRef.current = request.invoiceNumber

        // !res 도 함께 본다 — 위 분기에서 failure 대입이 빠져도 응답 없는 성공으로 넘어가지 않게, 단언 없이 타입을 좁힌다
        if (failure || !res) {
          const message = failure || MESSAGES.scanEmpty
          setRows([])
          rowsRef.current = []
          setResultText(toResultText(res?.scanResult, barcode, message))
          printLabel({ barcode, result: undefined, missMatch: { triggered: true, message }, orderList: [] })
          throw scanFailureError(message)
        }

        const scanRes = res
        const nextRows = toInboundScanRows(scanRes)
        const message = scanRes.message ?? ''
        setRows(nextRows)
        rowsRef.current = nextRows
        setResultText(toResultText(scanRes.scanResult, nextRows[0]?.INB_TRS_INVOICE ?? request.invoiceNumber, message))
        printLabel({ barcode, result: nextRows[0], missMatch: { triggered: false, message }, orderList: toInboundOrderLabels(scanRes) })
        if (message) scanNotice.invalid(message)
        else scanNotice.success(MESSAGES.scanDone(request.invoiceNumber))
        return false
      } finally {
        scanningRef.current = false
        setScanning(false)
      }
    },
    [scanAsync, printLabel, autoRecordFor],
  )

  const save = useCallback(async (): Promise<false> => {
    const org = orgRef.current
    if (!org) throw scanInvalidError(MESSAGES.saveNoOrgInvoice)
    if (mediaRef.current.isRecording) throw scanInvalidError(MESSAGES.saveWhileRecording)
    const list = attachmentsRef.current
    if (list.some((a) => a.status === 'uploading')) throw scanInvalidError(MESSAGES.saveWhileUploading)
    const done = list.filter((a): a is InboundAttachment & { key: string } => a.status === 'done' && a.key != null)
    if (!done.length) throw scanInvalidError(MESSAGES.saveNoAttachment)
    if (done.length > INBOUND_ATTACHMENT_MAX) throw scanInvalidError(MESSAGES.saveTooMany(done.length))

    let historyFound: boolean
    let truncated: boolean
    try {
      ;({ historyFound, truncated } = await saveAsync({
        originalInvoiceNumber: org,
        files: done.map((a) => ({ key: a.key, resourceType: a.resourceType })),
      }))
    } catch (e) {
      const failure = apiErrorText(e, MESSAGES.saveFailed)
      // 1.0 은 저장 실패도 스캔 실패와 같은 결과(triggered)로 받아 마지막 입력으로 송장미매칭 라벨을 찍었다(GL09.label.test K9 'U1 확정') — 그대로 둔다.
      printLabel({ barcode: lastBarcodeRef.current, result: undefined, missMatch: { triggered: true, message: failure }, orderList: [] })
      throw scanFailureError(failure)
    }
    // 이력이 없으면 서버가 아무것도 바꾸지 않았다 — 첨부를 남겨 둔다(다른 송장을 스캔한 뒤 다시 저장할 수 있게).
    if (!historyFound) throw scanFailureError(MESSAGES.saveNoHistory(org))

    const saved = new Set(done.map((a) => a.file))
    setAttachments((prev) => prev.filter((a) => !saved.has(a.file)))
    mediaRef.current.discard([...saved])
    // FILE_ID 20자 절단(1.0 과 같다) — 서버는 성공했지만 일부 첨부가 이력에 붙지 않았다. 다시 저장해도 붙지 않으므로 첨부는 비운다.
    if (truncated) scanNotice.invalid(MESSAGES.saveTruncated(done.length, org))
    else scanNotice.success(MESSAGES.saveDone(done.length, org))
    return false
  }, [saveAsync, printLabel])

  const startRecording = useCallback((): false | undefined => {
    const m = mediaRef.current
    if (!m.isCameraReady) throw scanInvalidError(m.cameraError ?? MESSAGES.cameraNotReady)
    if (m.isRecording) {
      scanNotice.invalid(MESSAGES.alreadyRecording)
      return false
    }
    m.startRecording()
    return undefined
  }, [])

  const stopRecording = useCallback(async (): Promise<string | false> => {
    const m = mediaRef.current
    if (!m.isRecording) {
      scanNotice.invalid(MESSAGES.notRecording)
      return false
    }
    await m.stopRecording()
    return MESSAGES.recordStopped
  }, [])

  const viewRows = useMemo(() => rows.map(toViewRow), [rows])
  const files = useMemo(
    () => attachments.map((a) => `${a.fileName}${a.status === 'uploading' ? MESSAGES.attachUploadingSuffix : a.status === 'failed' ? MESSAGES.attachFailedSuffix : ''}`),
    [attachments],
  )

  const caption = media.cameraError ?? (media.isCameraReady ? MESSAGES.cameraReady : MESSAGES.cameraConnecting)

  return {
    invoiceField: { value: invoice, onChange: setInvoice },
    autoRecordField: { value: autoRecord, onChange: setAutoRecord },
    secondsField: { value: seconds, onChange: (v) => setSeconds(digits(v)) },
    widthField: { value: width, onChange: (v) => setWidth(digits(v)) },
    heightField: { value: height, onChange: (v) => setHeight(digits(v)) },
    rows: viewRows,
    scanning,
    attachText: toAttachText(attachments),
    orgInvoiceText: orgInvoice ? MESSAGES.orgInvoice(orgInvoice) : MESSAGES.orgInvoiceEmpty,
    resultText,
    recording: {
      // <video> 는 카메라 오류가 아니면 늘 그린다 — 스트림이 늦게 와도 훅이 이 요소에 꽂는다(반품작업_신규 2.0 과 같다).
      preview: media.cameraError ? undefined : (
        <video ref={media.videoRef} autoPlay muted playsInline style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
      ),
      recording: media.isRecording,
      caption,
      files,
      onCapture: media.capture,
      onRemoveFile: removeAttachment,
    },
    scan,
    save,
    startRecording,
    stopRecording,
  }
}
